/* global YT, Vimeo */

/**
 * Normalizes Vimeo URLs for oEmbed (player.vimeo.com → vimeo.com/{id}).
 * @param {string} url
 * @returns {string}
 */
function normalizeVimeoOembedUrl(url) {
  const playerMatch = url.match(/player\.vimeo\.com\/video\/(\d+)/);
  if (!playerMatch) return url;

  const id = playerMatch[1];
  const hashMatch = url.match(/[?&]h=([^&]+)/);
  return hashMatch ? `https://vimeo.com/${id}?h=${hashMatch[1]}` : `https://vimeo.com/${id}`;
}

/**
 * Builds a Vimeo CDN thumbnail URL at a target width.
 * @param {string} baseUrl - thumbnail_url from oEmbed
 * @param {number} width
 * @param {'landscape'|'portrait'} aspectRatio
 * @returns {string}
 */
function vimeoThumbnailAtWidth(baseUrl, width, aspectRatio = 'landscape') {
  if (!baseUrl?.includes('vimeocdn.com')) return baseUrl;
  const height = aspectRatio === 'portrait'
    ? Math.round((width * 16) / 9)
    : Math.round((width * 9) / 16);
  return baseUrl.replace(/_\d+(x\d+)?(\.(jpg|jpeg|webp|png))(\?.*)?$/i, `_${width}x${height}$2$3`);
}

/**
 * @param {string} baseUrl
 * @param {'landscape'|'portrait'} aspectRatio
 * @returns {{ src: string, srcset: string, sizes: string }|null}
 */
function vimeoPosterSources(baseUrl, aspectRatio = 'landscape') {
  if (!baseUrl?.includes('vimeocdn.com')) return null;

  const maxWidth = aspectRatio === 'portrait' ? 720 : 1280;

  return {
    src: vimeoThumbnailAtWidth(baseUrl, maxWidth, aspectRatio),
    srcset: [
      `${vimeoThumbnailAtWidth(baseUrl, 640, aspectRatio)} 640w`,
      `${vimeoThumbnailAtWidth(baseUrl, maxWidth, aspectRatio)} ${maxWidth}w`,
      `${vimeoThumbnailAtWidth(baseUrl, aspectRatio === 'portrait' ? 1080 : 1920, aspectRatio)} ${aspectRatio === 'portrait' ? 1080 : 1920}w`,
    ].join(', '),
    sizes: aspectRatio === 'portrait'
      ? '(max-width: 640px) 100vw, 480px'
      : '(max-width: 640px) 100vw, (max-width: 1200px) 90vw, 1280px',
  };
}

/**
 * @param {string} url
 * @param {'landscape'|'portrait'} aspectRatio
 * @returns {Promise<{ src: string, srcset?: string, sizes?: string }|null>}
 */
async function fetchExternalPosterUrl(url, aspectRatio = 'landscape') {
  if (!url) return null;

  if (url.includes('youtu.be') || url.includes('youtube.com')) {
    let videoId = '';
    if (url.includes('v=')) {
      videoId = url.split('v=').pop().split('&')[0];
    } else {
      videoId = url.split('?')[0].split('/').pop();
    }
    if (!videoId) return null;

    return {
      src: `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
      srcset: [
        `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg 480w`,
        `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg 1280w`,
      ].join(', '),
      sizes: '(max-width: 640px) 100vw, 1280px',
    };
  }

  if (url.includes('vimeo.com')) {
    const oembedUrl = normalizeVimeoOembedUrl(url);
    const oembedWidth = aspectRatio === 'portrait' ? 720 : 1280;
    const oembedHeight = aspectRatio === 'portrait' ? 1280 : 720;
    try {
      const res = await fetch(
        `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(oembedUrl)}&width=${oembedWidth}&height=${oembedHeight}`
      );
      if (!res.ok) return null;
      const data = await res.json();
      const base = data?.thumbnail_url;
      if (!base) return null;
      return vimeoPosterSources(base, aspectRatio) || { src: base };
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * @param {HTMLImageElement} posterImg
 * @param {string} [altText]
 */
function applyExternalPoster(posterImg, altText) {
  if (!posterImg) return;

  posterImg.alt = altText || posterImg.alt || '';
  posterImg.removeAttribute('hidden');
  posterImg.dataset.posterLoaded = 'true';
}

/**
 * Fetches Vimeo/YouTube thumbnail for a poster slot (runs on page load, not on play).
 * @param {HTMLImageElement} posterImg
 * @param {string} [altText]
 */
async function loadExternalPosterForImage(posterImg, altText) {
  if (!posterImg || posterImg.dataset.posterLoaded === 'true' || posterImg.dataset.posterLoading === 'true') {
    return;
  }

  const url = posterImg.dataset.videoUrl;
  if (!url) return;

  posterImg.dataset.posterLoading = 'true';

  const aspectRatio = posterImg.dataset.videoAspectRatio === 'portrait' ? 'portrait' : 'landscape';
  const poster = await fetchExternalPosterUrl(url, aspectRatio);
  if (poster?.src) {
    posterImg.src = poster.src;
    if (poster.srcset) {
      posterImg.srcset = poster.srcset;
      posterImg.sizes = poster.sizes || '100vw';
    }
    applyExternalPoster(posterImg, altText);
  }

  delete posterImg.dataset.posterLoading;
}

function initExternalVideoPosters(root = document) {
  root.querySelectorAll('.js-video-poster-image[data-video-url]').forEach((posterImg) => {
    loadExternalPosterForImage(posterImg);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initExternalVideoPosters());
} else {
  initExternalVideoPosters();
}

if (!customElements.get('video-component')) {
  class Video extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';
    }

    connectedCallback() {
      this.loadExternalPoster();
      this.init();
    }

    /**
     * Ensures poster is loaded when the player mounts (fallback if page-load fetch missed).
     */
    loadExternalPoster() {
      const section = this.closest('.video-section, .media-with-text');
      const posterImg = section?.querySelector('.js-video-poster-image');
      if (!posterImg) return;

      loadExternalPosterForImage(posterImg, this.dataset.description);
    }

    init() {
      const url = this.dataset.videoUrl;

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          if (url.includes('v=')) {
            this.videoId = url.split('v=').pop().split('&')[0];
          } else {
            this.videoId = url.split('?')[0].split('/').pop();
          }

          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          const idMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
          this.videoId = idMatch ? idMatch[1] : url.split('?')[0].split('/').pop();
          const hashMatch = url.match(/[?&]h=([^&]+)/) || url.match(/vimeo\.com\/\d+\/([a-f0-9]+)/i);
          this.vimeoHash = hashMatch ? hashMatch[1] : null;
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }

      // Allow for videos which haven't played to play on tap
      if (this.background) {
        this.closest('.video-section').addEventListener('click', this.play.bind(this));
      }
    }

    /**
     * Loads a player API script.
     * @param {string} src - Url of script to load.
     */
    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err);  // eslint-disable-line

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
      }

      document.body.appendChild(s);
    }

    /**
     * Initialises a YouTube video.
     */
    initYouTube() {
      const initYTPlayer = () => {
        const isPortrait = this.dataset.aspectRatio === 'portrait';
        YT.ready(() => {
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: isPortrait ? '720' : '1280',
            height: isPortrait ? '1280' : '720',
            playerVars: {
              controls: this.background ? 0 : 1,
              disablekb: this.background ? 0 : 1,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this)
            }
          });
        });
      };

      if (!window.YT) {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer.bind(this));
        this.loadScript('//www.youtube.com/iframe_api');
      } else {
        initYTPlayer();
      }
    }

    /**
     * Initialises a Vimeo video.
     */
    initVimeo() {
      const initVimeoPlayer = async () => {
        const isPortrait = this.dataset.aspectRatio === 'portrait';
        const playerOptions = {
          width: isPortrait ? '720' : '1280',
          height: isPortrait ? '1280' : '720',
          autoplay: this.autoplay,
          background: this.background,
          keyboard: !this.background,
          muted: this.autoplay
        };

        if (this.vimeoHash) {
          playerOptions.url = `https://player.vimeo.com/video/${this.videoId}?h=${this.vimeoHash}`;
        } else {
          playerOptions.id = this.videoId;
        }

        this.player = new Vimeo.Player(this, playerOptions);

        await this.player.ready();
        this.setupIframe('js-vimeo');

        this.player.on('pause', () => {
          if (this.inViewport) this.pausedByUser = true;
        });

        this.player.on('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
          this.dispatchEvent(new CustomEvent('on:media:play', {
            detail: {
              player: this.iframe
            },
            bubbles: true
          }));
        });

        this.play();
      };

      if (!window.Vimeo) {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer.bind(this));
        this.loadScript('//player.vimeo.com/api/player.js');
      } else {
        initVimeoPlayer();
      }
    }

    /**
     * Initialises a video tag.
     */
    initVideoTag() {
      this.player = this.querySelector('video');

      this.player.addEventListener('pause', () => {
        if (this.inViewport) this.pausedByUser = true;
      });

      this.player.addEventListener('play', () => {
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.player
          },
          bubbles: true
        }));
      });

      // If the video is already playing
      if (this.player.currentTime > 0 && !this.player.paused && !this.player.ended
        && this.player.readyState > 2) {
        this.closest('.video-section').classList.add('video-section--played');
      } else {
        this.player.addEventListener('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
        });

        this.addObserver(this.player);

        // Mute the video if a click didn't trigger its load
        const deferredMedia = this.closest('deferred-media');
        if (!deferredMedia || deferredMedia.loadTrigger !== 'click') {
          this.player.muted = true;
        }

        this.play();
      }
    }

    /**
     * Handles a YouTube ready event.
     */
    handleYTReady() {
      this.setupIframe('js-youtube');

      if (this.autoplay) this.player.mute();
      this.play();
    }

    /**
     * Handles a YouTube state change event.
     * @param {object} e - Event object.
     */
    handleYTStateChange(e) {
      if (e.data === YT.PlayerState.PAUSED) {
        if (this.inViewport) this.pausedByUser = true;
      } else if (e.data === YT.PlayerState.ENDED) {
        // Looping this way rather than 'loop' API parameter to avoid flash of black background
        if (this.background) this.player.playVideo();
      } else if (e.data === YT.PlayerState.PLAYING) {
        this.closest('.video-section').classList.add('video-section--played');
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.iframe
          },
          bubbles: true
        }));
      }
    }

    /**
     * Sets up the injected iframe.
     * @param {string} jsClass - Class name to add to the iframe.
     */
    setupIframe(jsClass) {
      // Enable iframe to cover entire container height.
      if (this.autoplay && !this.naturalWidth) {
        const widthRatio = this.dataset.aspectRatio === 'portrait' ? 9 / 16 : 16 / 9;
        this.style.width = `${this.clientHeight * widthRatio}px`;

        window.addEventListener('resize', window.debounce(() => {
          this.style.width = `${this.clientHeight * widthRatio}px`;
        }, 200));
      }

      this.iframe = this.querySelector('iframe');
      this.iframe.title = this.dataset.description;
      this.iframe.classList.add(jsClass);
      this.addObserver(this.iframe);
    }

    /**
     * Plays the video when scrolled into view (if not paused by the user).
     */
    play() {
      if (!this.player || this.pausedByUser) return;

      if (this.type === 'youtube') {
        this.player.playVideo();
      } else {
        this.player.play();
      }
    }

    /**
     * Pauses the video when scrolled out of view (if playing).
     */
    pause() {
      if (!this.player) return;

      if (this.type === 'youtube') {
        if (this.player.getPlayerState() !== 2) {
          this.player.pauseVideo();
          this.pausedByUser = false;
        }
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => {
          if (!paused) {
            this.player.pause();
            this.pausedByUser = false;
          }
        });
      } else if (!this.player.paused) {
        this.player.pause();
        this.pausedByUser = false;
      }
    }

    /**
     * Adds an observer to pause the video when not in the viewport.
     * @param {Element} el - Element to observe.
     */
    addObserver(el) {
      if ('IntersectionObserver' in window === false) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.inViewport = true;
            this.play();
          } else {
            this.inViewport = false;
            this.pause();
          }
        }, { rootMargin: '0px 0px 200px 0px' });
      });

      observer.observe(el);
    }
  }

  customElements.define('video-component', Video);
}
